import { useEffect, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { GroundingFiles } from "@/components/ui/grounding-files";
import GroundingFileView from "@/components/ui/grounding-file-view";
import StatusMessage from "@/components/ui/status-message";

import useRealTime from "@/hooks/useRealtime";
import useAudioRecorder from "@/hooks/useAudioRecorder";
import useAudioPlayer from "@/hooks/useAudioPlayer";

import { GroundingFile, ToolResult } from "./types";

import logo from "./assets/logobot.png";
import logo1 from "./assets/toriri.png";
import logo2 from "./assets/toriri2.png";

function App() {
    const [isRecording, setIsRecording] = useState(false);
    const [groundingFiles, setGroundingFiles] = useState<GroundingFile[]>([]);
    const [selectedFile, setSelectedFile] = useState<GroundingFile | null>(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [currentImage, setCurrentImage] = useState(0);
    const images = [logo1, logo2];

    useEffect(() => {
        let interval: NodeJS.Timeout | undefined;

        if (isSpeaking) {
            interval = setInterval(() => {
                setCurrentImage(prev => (prev + 1) % images.length);
            }, 400);
        } else {
            setCurrentImage(0);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [isSpeaking, images.length]);

    const { startSession, addUserAudio, inputAudioBufferClear } = useRealTime({
        onWebSocketOpen: () => console.log("WebSocket connection opened"),
        onWebSocketClose: () => console.log("WebSocket connection closed"),
        onWebSocketError: event => console.error("WebSocket error:", event),
        onReceivedError: message => console.error("error", message),
        onReceivedResponseAudioDelta: message => {
            if (isRecording) {
                playAudio(message.delta);
                const sampleRate = 24000;
                const durationSec = message.delta.length / sampleRate;

                setIsSpeaking(true);
                if ((window as any).speakingTimer) {
                    clearTimeout((window as any).speakingTimer);
                }

                (window as any).speakingTimer = setTimeout(() => {
                    setIsSpeaking(false);
                }, durationSec * 1000);
            }
        },

        onReceivedInputAudioBufferSpeechStarted: () => {
            stopAudioPlayer();
            setIsSpeaking(false);
        },
        onReceivedExtensionMiddleTierToolResponse: message => {
            const result: ToolResult = JSON.parse(message.tool_result);
            const files: GroundingFile[] = result.sources.map(x => {
                return { id: x.chunk_id, name: x.title, content: x.chunk };
            });

            setGroundingFiles(prev => [...prev, ...files]);
        }
    });

    const { reset: resetAudioPlayer, play: playAudio, stop: stopAudioPlayer } = useAudioPlayer();
    const { start: startAudioRecording, stop: stopAudioRecording } = useAudioRecorder({ onAudioRecorded: addUserAudio });

    const onToggleListening = async () => {
        if (!isRecording) {
            startSession();
            await startAudioRecording();
            resetAudioPlayer();
            setIsRecording(true);
        } else {
            await stopAudioRecording();
            stopAudioPlayer();
            inputAudioBufferClear();
            setIsRecording(false);
            setIsSpeaking(false);
        }
    };

    const { t } = useTranslation();

    return (
        <div className="flex min-h-screen flex-col bg-gradient-to-br from-blue-50 via-white to-blue-100 text-gray-900">
            {/* ヘッダー */}
            <header className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between bg-white px-6 py-4 shadow-md">
                <div className="flex items-center gap-3">
                    <img src={logo} alt="Logo" className="h-10 w-10" />
                    <span className="text-lg font-bold text-gray-700">滋賀県専属BOT</span>
                </div>
            </header>

            {/* メイン */}
            <main className="flex flex-grow items-center justify-center px-4 pt-24">
                <div className="w-full max-w-2xl rounded-xl bg-white p-10 text-center shadow-lg">
                    <h1 className="mb-6 bg-gradient-to-r from-blue-700 to-blue-500 bg-clip-text text-4xl font-bold text-transparent md:text-5xl">
                        {t("app.title")}
                    </h1>

                    <div className="flex flex-col items-center">
                        {/* Bot画像（角丸なし / アニメーションあり） */}
                        <div className="relative mb-6">
                            <img
                                src={images[currentImage]}
                                alt="Bot"
                                className={`h-56 w-56 transition-transform duration-700 ${isSpeaking ? "scale-105" : ""}`}
                            />
                        </div>

                        {/* 録音ボタン */}
                        <Button
                            onClick={onToggleListening}
                            className={`relative h-12 w-60 overflow-hidden rounded-xl font-bold text-white shadow-md transition-all ${
                                isRecording ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
                            }`}
                            aria-label={isRecording ? t("app.stopRecording") : t("app.startRecording")}
                        >
                            {isRecording ? (
                                <>
                                    <MicOff className="mr-2 h-4 w-4" />
                                    {t("app.stopConversation")}
                                </>
                            ) : (
                                <>
                                    <Mic className="mr-2 h-6 w-6" />
                                    {t("app.startRecording")}
                                </>
                            )}
                        </Button>

                        {/* ステータスメッセージ */}
                        <StatusMessage isRecording={isRecording} className="mt-4" />
                    </div>

                    {/* Grounding files とビュー */}
                    <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div className="md:col-span-2">
                            <GroundingFiles files={groundingFiles} onSelected={setSelectedFile} />
                        </div>
                        <aside className="hidden md:block">
                            {selectedFile && <GroundingFileView groundingFile={selectedFile} onClosed={() => setSelectedFile(null)} />}
                        </aside>
                    </div>
                </div>
            </main>

            {/* フッター */}
            <footer className="mt-auto bg-gradient-to-r from-blue-400 to-blue-300 py-6 text-center text-white">
                <p className="text-sm">{t("app.footer")}</p>
            </footer>
        </div>
    );
}

export default App;
